(() => {
  'use strict';

  if (window.BloomBundleSave) {
    window.BloomBundleSave.init(document);
    return;
  }

  const integer = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? Math.max(0, Math.floor(number)) : fallback;
  };
  const variantId = value => /^\d+$/.test(String(value || '')) ? String(value) : '';

  class BundleSave {
    constructor(root) {
      this.root = root;
      this.kind = 'whips';
      this.route = 'custom';
      this.target = 4;
      this.whipTarget = 4;
      this.upgrade = false;
      this.filter = 'all';
      this.search = '';
      this.opened = false;
      this.busy = false;
      this.destroyed = false;
      this.states = Object.fromEntries(['custom', 'sprays', 'wax', 'mixed'].map(route => [route, {
        selections: { whips: new Map(), sprays: new Map(), wax: new Map() }
      }]));
      this.selections = this.states.custom.selections;
      this.messages = {};
      try {
        const messages = JSON.parse(root.dataset.bbI18n || '{}');
        Object.keys(messages).forEach(key => {
          if (typeof messages[key] === 'string') this.messages[key] = messages[key];
        });
      } catch (_) {}
      this.waxBoxTotal = integer(root.dataset.waxBoxTotal, 1995);
      this.waxEligiblePrice = integer(root.dataset.waxEligiblePrice, 449);
      this.locale = root.dataset.locale || document.documentElement.lang || 'en';
      this.currency = root.dataset.currency || 'GBP';
      try {
        this.formatter = new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency });
      } catch (_) {
        this.formatter = new Intl.NumberFormat('en', { style: 'currency', currency: 'GBP' });
      }
      const localeRoot = root.dataset.root || '/';
      this.localeRoot = localeRoot.startsWith('/') && !localeRoot.startsWith('//')
        ? `${localeRoot.replace(/\/+$/, '')}/` : '/';
      this.cards = Array.from(root.querySelectorAll('[data-bb-card]')).map((element, index) => ({
        element,
        index,
        input: element.querySelector('[data-bb-qty]'),
        variant: element.querySelector('[data-bb-variant]'),
        kind: ['whips', 'sprays', 'wax'].includes(element.dataset.kind) ? element.dataset.kind : 'whips',
        title: element.dataset.title || '',
        search: (element.dataset.search || element.dataset.title || '').toLocaleLowerCase(this.locale),
        families: (element.dataset.family || '').split('|'),
        image: element.dataset.image || ''
      }));
      this.originalDisabled = new Map(Array.from(root.querySelectorAll('button, input, select, textarea'))
        .map(control => [control, Boolean(control.disabled)]));
      this.disabledBeforeRequest = new Map();
      this.handleClick = this.onClick.bind(this);
      this.handleInput = this.onInput.bind(this);
      this.handleChange = this.onChange.bind(this);
      this.viewportFrame = 0;
      this.handleViewport = () => {
        if (this.destroyed || this.viewportFrame) return;
        this.viewportFrame = window.requestAnimationFrame(() => {
          this.viewportFrame = 0;
          this.updateSummaryViewport();
        });
      };
      root.addEventListener('click', this.handleClick);
      root.addEventListener('input', this.handleInput);
      root.addEventListener('change', this.handleChange);
      window.addEventListener('resize', this.handleViewport, { passive: true });
      window.addEventListener('scroll', this.handleViewport, { passive: true });
      root.style.setProperty('--bb-mobile-bottom', window.Shopify && window.Shopify.theme
        && window.Shopify.theme.role && window.Shopify.theme.role !== 'main' ? '60px' : '0px');
      this.render();
    }

    message(key, values = {}) {
      return (this.messages[key] || '').replace(/\{(\w+)\}/g, (match, name) =>
        Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : match);
    }

    money(cents) { return this.formatter.format(cents / 100); }

    setText(selector, value) {
      this.root.querySelectorAll(selector).forEach(element => { element.textContent = value; });
    }

    notice(key, error = false) {
      this.root.querySelectorAll(error ? '[data-bb-error]' : '[data-bb-status]').forEach(element => {
        element.textContent = key ? this.message(key) : '';
        element.hidden = !key;
      });
    }

    details(card, requestedId) {
      let option = null;
      if (card.variant) {
        option = requestedId
          ? Array.from(card.variant.options).find(item => String(item.value) === String(requestedId))
          : card.variant.selectedOptions[0];
        if (!option) return { id: '', price: 0, max: 0, available: false };
      }
      const source = option || card.element;
      const id = variantId(option ? option.value : card.element.dataset.id);
      if (requestedId && id !== String(requestedId)) return { id: '', price: 0, max: 0, available: false };
      const max = integer(source.dataset.max, 8);
      return {
        id,
        price: integer(source.dataset.price, integer(card.element.dataset.price)),
        max,
        available: source.dataset.available === 'true' && max > 0 && Boolean(id)
      };
    }

    quotas() {
      if (this.route === 'sprays') return { sprays: 3 };
      if (this.route === 'wax') return { wax: 5 };
      if (this.route === 'mixed') return { whips: 4, sprays: 3 };
      return this.upgrade ? { whips: this.whipTarget, wax: 3 } : { whips: this.whipTarget };
    }

    activeKinds() { return Object.keys(this.quotas()); }

    count(kind) {
      const kinds = kind ? [kind] : this.activeKinds();
      return kinds.reduce((sum, category) => sum + Array.from(this.selections[category].values())
        .reduce((quantity, item) => quantity + item.quantity, 0), 0);
    }

    quantity(card) {
      const entry = this.selections[card.kind].get(this.details(card).id);
      return entry ? entry.quantity : 0;
    }

    validSelection() {
      const quotas = this.quotas();
      const quantities = new Map();
      return this.activeKinds().every(kind => this.count(kind) === quotas[kind]
        && Array.from(this.selections[kind].values()).every(entry => {
          const details = this.details(entry.card, entry.id);
          const quantity = (quantities.get(entry.id) || 0) + entry.quantity;
          quantities.set(entry.id, quantity);
          return details.available && entry.quantity > 0 && quantity <= details.max
            && (this.route !== 'wax' || details.price === this.waxEligiblePrice);
        }));
    }

    updateQuantity(card, requested) {
      if (this.busy || card.kind !== this.kind || !this.activeKinds().includes(card.kind)) return;
      const details = this.details(card);
      const quantity = this.quantity(card);
      const remaining = Math.max(0, this.quotas()[card.kind] - this.count(card.kind) + quantity);
      const elsewhere = this.activeKinds().filter(kind => kind !== card.kind).reduce((sum, kind) =>
        sum + ((this.selections[kind].get(details.id) || {}).quantity || 0), 0);
      const available = details.available && (this.route !== 'wax' || details.price === this.waxEligiblePrice);
      const desired = integer(requested);
      const next = available ? Math.max(0, Math.min(desired, details.max - elsewhere, remaining)) : 0;
      if (next) this.selections[card.kind].set(details.id, { id: details.id, quantity: next, card });
      else this.selections[card.kind].delete(details.id);
      this.notice('', true);
      this.notice(!available && desired > 0 ? 'unavailable' : desired > remaining ? 'limit' : '');
      this.render();
    }

    resetFilters() {
      this.filter = 'all';
      this.search = '';
      this.root.querySelectorAll('[data-bb-search]').forEach(input => { input.value = ''; });
    }

    focusBuilder() {
      const heading = this.root.querySelector('[data-bb-builder-title]');
      if (!heading) return;
      heading.focus({ preventScroll: true });
      heading.scrollIntoView({ behavior: 'auto', block: 'start' });
      this.handleViewport();
    }

    updateSummaryViewport() {
      if (this.destroyed || !this.opened || window.innerWidth < 750) return;
      const summary = this.root.querySelector('.bb-summary');
      if (!summary) return;
      const stickyTop = parseFloat(window.getComputedStyle(summary).top) || 0;
      const top = Math.ceil(Math.max(stickyTop, summary.getBoundingClientRect().top));
      this.root.style.setProperty('--bb-summary-top', `${top}px`);
    }

    open(route) {
      if (this.busy || !Object.prototype.hasOwnProperty.call(this.states, route)) return false;
      this.route = route;
      this.selections = this.states[route].selections;
      this.kind = this.activeKinds()[0];
      if (route === 'wax') this.cards.filter(card => card.kind === 'wax' && card.variant).forEach(card => {
        if (this.details(card).price !== this.waxEligiblePrice) {
          const eligible = Array.from(card.variant.options).find(option =>
            integer(option.dataset.price) === this.waxEligiblePrice && option.dataset.available === 'true');
          if (eligible) card.variant.value = eligible.value;
        }
      });
      this.opened = true;
      this.resetFilters();
      this.notice('', true);
      this.notice('');
      this.render();
      this.focusBuilder();
      return true;
    }

    setSize(size) {
      if (this.busy || this.route !== 'custom' || ![4, 8].includes(Number(size))) return;
      const target = Number(size);
      if (this.count('whips') > target) {
        this.notice(this.messages.reduce_size ? 'reduce_size' : 'limit');
        return;
      }
      this.whipTarget = target;
      this.notice('');
      this.render();
    }

    clear() {
      if (this.busy) return;
      Object.values(this.selections).forEach(selection => selection.clear());
      this.notice('', true);
      this.notice('');
      this.render();
    }

    setKind(kind) {
      if (this.busy || !this.activeKinds().includes(kind)) return;
      this.kind = kind;
      this.resetFilters();
      this.notice('');
      this.render();
    }

    setUpgrade(enabled) {
      if (this.busy || this.route !== 'custom') return;
      this.upgrade = Boolean(enabled);
      if (!this.upgrade) {
        this.selections.wax.clear();
        if (this.kind === 'wax') this.kind = 'whips';
      }
      this.resetFilters();
      this.notice('', true);
      this.notice('');
      this.render();
    }

    onClick(event) {
      if (!event.target || !event.target.closest) return;
      if (this.busy) {
        const interactive = event.target.closest('button, input, select, textarea, a');
        if (interactive && this.root.contains(interactive)) event.preventDefault();
        return;
      }
      const control = event.target.closest('[data-bb-open], [data-bb-back], button[data-bb-kind], '
        + '[data-bb-size], [data-bb-minus], [data-bb-plus], [data-bb-filter], [data-bb-clear], '
        + '[data-bb-add], [data-bb-slot-remove]');
      if (!control || !this.root.contains(control)) return;
      event.preventDefault();
      if (control.disabled) return;
      if (control.hasAttribute('data-bb-open')) { this.open(control.dataset.bbOpen); return; }
      if (control.hasAttribute('data-bb-back')) {
        this.opened = false;
        this.render();
        const button = this.root.querySelector(`[data-bb-open="${this.route}"]`);
        if (button) button.focus({ preventScroll: true });
        this.root.scrollIntoView({ behavior: 'auto', block: 'start' });
      } else if (control.hasAttribute('data-bb-size')) this.setSize(control.dataset.bbSize);
      else if (control.hasAttribute('data-bb-kind')) this.setKind(control.dataset.bbKind);
      else if (control.hasAttribute('data-bb-clear')) this.clear();
      else if (control.hasAttribute('data-bb-add')) this.add();
      else if (control.hasAttribute('data-bb-slot-remove')) {
        const id = control.dataset.bbSlotRemove;
        const kind = control.dataset.bbSlotKind;
        if (!this.activeKinds().includes(kind)) return;
        const entry = this.selections[kind].get(id);
        if (entry) {
          if (entry.quantity > 1) entry.quantity--;
          else this.selections[kind].delete(id);
          this.notice('', true);
          this.notice('');
          this.render();
          const next = this.root.querySelector('[data-bb-slot-remove]')
            || this.root.querySelector('[data-bb-builder-title]');
          if (next) next.focus({ preventScroll: true });
        }
      } else if (control.hasAttribute('data-bb-filter')) {
        this.filter = control.dataset.bbFilter || 'all';
        this.render();
      } else {
        const element = control.closest('[data-bb-card]');
        const card = this.cards.find(item => item.element === element);
        if (card) this.updateQuantity(card, this.quantity(card) + (control.hasAttribute('data-bb-plus') ? 1 : -1));
      }
    }

    onInput(event) {
      if (this.busy) return;
      if (event.target.matches('[data-bb-search]')) {
        this.search = event.target.value.trim().toLocaleLowerCase(this.locale);
        this.render();
      } else if (event.target.matches('[data-bb-qty]')) {
        const card = this.cards.find(item => item.input === event.target);
        if (card) this.updateQuantity(card, event.target.value);
      }
    }

    onChange(event) {
      if (this.busy) return;
      if (event.target.matches('[data-bb-upgrade]')) this.setUpgrade(event.target.checked);
      else if (event.target.matches('[data-bb-variant]')) {
        this.notice('', true);
        this.notice('');
        this.render();
      } else if (event.target.matches('[data-bb-qty]')) {
        const card = this.cards.find(item => item.input === event.target);
        if (card) this.updateQuantity(card, event.target.value);
      }
    }

    slots() {
      const fragment = document.createDocumentFragment();
      const quotas = this.quotas();
      this.activeKinds().forEach(kind => {
        let filled = 0;
        this.selections[kind].forEach(entry => {
        for (let index = 0; index < entry.quantity; index++) {
          const slot = document.createElement('li');
          slot.className = 'bb-slot';
          if (entry.card.image) {
            const image = document.createElement('img');
            image.src = entry.card.image;
            image.alt = '';
            image.width = 54;
            image.height = 54;
            image.loading = 'lazy';
            slot.appendChild(image);
          }
          const label = document.createElement('span');
          label.className = 'bb-slot-label';
          label.textContent = entry.card.title;
          slot.appendChild(label);
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'bb-slot-remove';
          remove.dataset.bbSlotRemove = entry.id;
          remove.dataset.bbSlotKind = kind;
          remove.textContent = '×';
          remove.setAttribute('aria-label', this.message('remove', { title: entry.card.title }));
          remove.disabled = this.busy;
          slot.appendChild(remove);
          fragment.appendChild(slot);
          filled++;
        }
        });
      while (filled < quotas[kind]) {
        const slot = document.createElement('li');
        slot.className = 'bb-slot';
        const label = document.createElement('span');
        label.className = 'bb-slot-label';
        label.textContent = this.activeKinds().length > 1
          ? `${this.message('empty_slot')} · ${this.message(`${kind}_label`)}`
          : this.message('empty_slot');
        slot.appendChild(label);
        fragment.appendChild(slot);
        filled++;
      }
      });
      return fragment;
    }

    pricing() {
      let subtotal = 0;
      let saving = 0;
      this.activeKinds().forEach(kind => {
        const prices = [];
        this.selections[kind].forEach(entry => {
          const details = this.details(entry.card, entry.id);
          for (let index = 0; index < entry.quantity; index++) prices.push(details.price);
        });
        prices.sort((left, right) => left - right);
        subtotal += prices.reduce((sum, price) => sum + price, 0);
        const freeCount = kind === 'whips' ? Math.floor(prices.length / 4)
          : kind === 'sprays' ? Math.floor(prices.length / 3) : 0;
        saving += prices.slice(0, freeCount).reduce((sum, price) => sum + price, 0);
      });
      const total = this.route === 'wax' && this.validSelection() ? this.waxBoxTotal : subtotal - saving;
      if (this.route === 'wax') saving = Math.max(0, subtotal - total);
      const delivery = total >= 3000 ? 0 : 395;
      return { subtotal, saving, total, delivery, delivered: total + delivery };
    }

    render() {
      if (this.destroyed) return;
      const count = this.count();
      const quotas = this.quotas();
      const kinds = this.activeKinds();
      this.target = Object.values(quotas).reduce((sum, quantity) => sum + quantity, 0);
      if (!kinds.includes(this.kind)) this.kind = kinds[0];
      this.root.dataset.bbCount = String(count);
      this.root.dataset.bbKind = this.kind;
      this.root.dataset.bbRoute = this.route;
      this.root.dataset.bbTarget = String(this.target);
      this.root.querySelectorAll('[data-bb-hub]').forEach(element => { element.hidden = this.opened; });
      this.root.querySelectorAll('[data-bb-builder]').forEach(element => { element.hidden = !this.opened; });
      this.root.querySelectorAll('[data-bb-sizes]').forEach(element => { element.hidden = this.route !== 'custom'; });
      this.root.querySelectorAll('[data-bb-whip-filters]').forEach(element => { element.hidden = this.kind !== 'whips'; });
      this.root.querySelectorAll('[data-bb-upgrade-wrap]').forEach(element => { element.hidden = this.route !== 'custom'; });
      this.root.querySelectorAll('[data-bb-upgrade]').forEach(control => {
        control.checked = this.upgrade;
        control.disabled = this.busy;
      });
      this.root.querySelectorAll('[data-bb-category-tabs]').forEach(element => { element.hidden = kinds.length < 2; });
      this.root.querySelectorAll('button[data-bb-kind]').forEach(button => {
        const kind = button.dataset.bbKind;
        button.hidden = !kinds.includes(kind);
        button.disabled = this.busy;
        button.setAttribute('aria-pressed', String(kind === this.kind));
        button.querySelectorAll('[data-bb-kind-count]').forEach(element => {
          element.textContent = this.message('category_count', { count: this.count(kind), target: quotas[kind] || 0 });
        });
      });
      this.root.querySelectorAll('[data-bb-catalog]').forEach(element => {
        element.hidden = element.dataset.bbCatalog !== this.kind;
      });
      this.setText('[data-bb-builder-title]', this.message({ custom: 'custom_title', sprays: 'spray_title',
        wax: 'wax_title', mixed: 'mixed_title' }[this.route]));
      let visible = 0;
      this.cards.forEach(card => {
        const details = this.details(card);
        const quantity = this.quantity(card);
        const matches = card.kind === this.kind
          && (this.filter === 'all' || card.families.includes(this.filter))
          && (!this.search || card.search.includes(this.search))
          && (this.route !== 'wax' || details.price === this.waxEligiblePrice);
        card.element.hidden = !matches;
        card.element.dataset.selected = String(quantity > 0);
        card.element.querySelectorAll('[data-bb-card-price]').forEach(element => {
          element.textContent = this.money(details.price);
        });
        if (matches) visible++;
        const elsewhere = kinds.filter(kind => kind !== card.kind).reduce((sum, kind) =>
          sum + ((this.selections[kind].get(details.id) || {}).quantity || 0), 0);
        const maximum = Math.max(0, Math.min(details.max - elsewhere,
          (quotas[card.kind] || 0) - this.count(card.kind) + quantity));
        const available = details.available && kinds.includes(card.kind)
          && (this.route !== 'wax' || details.price === this.waxEligiblePrice);
        if (card.input) {
          card.input.value = String(quantity);
          card.input.setAttribute('value', String(quantity));
          card.input.min = '0';
          card.input.max = String(maximum);
          card.input.disabled = this.busy || !available;
        }
        card.element.querySelectorAll('[data-bb-minus]').forEach(button => {
          button.disabled = this.busy || quantity === 0;
        });
        card.element.querySelectorAll('[data-bb-plus]').forEach(button => {
          button.disabled = this.busy || !available || quantity >= maximum;
        });
        if (card.variant) card.variant.disabled = this.busy || this.originalDisabled.get(card.variant) === true;
      });
      this.root.querySelectorAll('[data-bb-filter]').forEach(button => {
        button.setAttribute('aria-pressed', String((button.dataset.bbFilter || 'all') === this.filter));
        button.disabled = this.busy || this.originalDisabled.get(button) === true;
      });
      this.root.querySelectorAll('[data-bb-size]').forEach(button => {
        const size = Number(button.dataset.bbSize);
        button.setAttribute('aria-pressed', String(size === this.whipTarget));
        button.disabled = this.busy || this.route !== 'custom' || (size === 4 && this.count('whips') > 4);
      });
      this.root.querySelectorAll('[data-bb-clear]').forEach(button => {
        button.disabled = this.busy || count === 0;
      });
      this.root.querySelectorAll('[data-bb-add]').forEach(button => {
        button.disabled = this.busy || !this.validSelection();
        button.textContent = this.message(this.busy ? 'adding' : 'add');
      });
      this.root.querySelectorAll('[data-bb-empty]').forEach(element => { element.hidden = visible > 0; });
      const slots = this.slots();
      this.root.querySelectorAll('[data-bb-slots]').forEach(list => {
        list.dataset.bbKind = kinds.length > 1 ? 'mixed' : this.kind;
        list.replaceChildren(slots.cloneNode(true));
      });
      this.root.querySelectorAll('[data-bb-progress]').forEach(progress => {
        progress.max = this.target;
        progress.value = Math.min(count, this.target);
        progress.setAttribute('aria-valuemax', String(this.target));
        progress.setAttribute('aria-valuenow', String(Math.min(count, this.target)));
      });
      const { subtotal, saving, total, delivery, delivered } = this.pricing();
      this.root.querySelectorAll('[data-bb-delivery-benefit]').forEach(element => {
        element.hidden = this.route !== 'mixed' || !this.validSelection() || total < 3000;
      });
      this.setText('[data-bb-breakdown]', kinds.map(kind => this.message('category_selected', {
        category: this.message(`${kind}_label`), count: this.count(kind), target: quotas[kind]
      })).join(' · '));
      const priceLabel = this.message(this.route === 'wax' || this.route === 'mixed' ? 'preview_total' : 'regular_total');
      this.setText('[data-bb-price-label]', priceLabel);
      this.root.querySelectorAll('.bb-summary-pricing').forEach(region => {
        region.setAttribute('aria-label', priceLabel);
      });
      this.setText('[data-bb-pricing-note]', this.message('cart_pricing'));
      this.setText('[data-bb-results]', this.message('results', { count: visible }));
      this.setText('[data-bb-count]', this.message('selected', { count, target: this.target }));
      this.setText('[data-bb-progress-text]', count === this.target
        ? this.message(this.validSelection() ? 'complete' : 'unavailable')
        : this.message('remaining', { count: Math.max(0, this.target - count) }));
      this.setText('[data-bb-subtotal]', this.money(subtotal));
      this.setText('[data-bb-saving]', this.money(saving));
      this.setText('[data-bb-total]', this.money(total));
      this.setText('[data-bb-delivery]', delivery === 0 ? this.message('free_delivery') : this.money(delivery));
      this.setText('[data-bb-delivered]', this.money(delivered));
      this.setText('[data-bb-cart-pricing]', this.message('cart_pricing'));
      this.root.setAttribute('aria-busy', String(this.busy));
      this.handleViewport();
    }

    drawerSectionIds(drawer) {
      let ids = [];
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        try {
          ids = drawer.getSectionsToRender().map(section => typeof section === 'string' ? section : section.id);
        } catch (_) {}
      }
      if (!ids.length && drawer) ids.push('cart-drawer');
      return [...new Set(ids.filter(id => typeof id === 'string' && /^[\w-]+$/.test(id)))].slice(0, 5);
    }

    sectionIds(drawer) {
      const ids = this.drawerSectionIds(drawer);
      if (!ids.includes('cart-icon-bubble')) {
        if (ids.length >= 5) ids.pop();
        ids.push('cart-icon-bubble');
      }
      return ids.slice(0, 5);
    }

    async add() {
      if (this.busy || this.destroyed || !this.opened || !this.validSelection()) return;
      const aggregated = new Map();
      this.activeKinds().forEach(kind => this.selections[kind].forEach(entry => {
        const item = aggregated.get(entry.id) || { id: entry.id, quantity: 0 };
        item.quantity += entry.quantity;
        aggregated.set(entry.id, item);
      }));
      const items = Array.from(aggregated.values());
      const drawer = document.querySelector('cart-drawer');
      const sections = this.sectionIds(drawer);
      if (drawer && typeof drawer.setActiveElement === 'function') {
        try { drawer.setActiveElement(document.activeElement); } catch (_) {}
      }
      this.busy = true;
      this.disabledBeforeRequest = new Map(Array.from(this.root.querySelectorAll('button, input, select, textarea'))
        .map(control => [control, Boolean(control.disabled)]));
      this.disabledBeforeRequest.forEach((_, control) => { control.disabled = true; });
      this.notice('', true);
      this.notice('adding');
      this.render();
      const controller = new AbortController();
      this.requestController = controller;
      const timeout = window.setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(`${this.localeRoot}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
          signal: controller.signal
        });
        if (!response.ok) throw new Error('Unconfirmed cart addition');
        const data = await response.json();
        if (!data || Number(data.status) >= 400 || !Array.isArray(data.items)
          || !items.every(item => data.items.some(added =>
            String(added.variant_id || added.id) === item.id && Number(added.quantity) >= item.quantity))) {
          throw new Error('Unconfirmed cart addition');
        }
        if (this.destroyed) return;
        this.activeKinds().forEach(kind => this.selections[kind].clear());
        this.notice('success');
        const drawerReady = drawer && typeof drawer.renderContents === 'function'
          && typeof drawer.open === 'function' && data.sections
          && this.drawerSectionIds(drawer).every(id => typeof data.sections[id] === 'string');
        if (drawerReady) {
          try {
            drawer.classList.remove('is-empty');
            await drawer.renderContents(data);
            drawer.open();
          } catch (_) {
            window.location.assign(`${this.localeRoot}cart`);
          }
        } else window.location.assign(`${this.localeRoot}cart`);
      } catch (_) {
        if (!this.destroyed) {
          this.notice('');
          this.notice('failed', true);
        }
      } finally {
        window.clearTimeout(timeout);
        this.requestController = null;
        this.busy = false;
        this.disabledBeforeRequest.forEach((disabled, control) => { control.disabled = disabled; });
        this.disabledBeforeRequest.clear();
        this.render();
      }
    }

    destroy() {
      this.destroyed = true;
      this.root.removeEventListener('click', this.handleClick);
      this.root.removeEventListener('input', this.handleInput);
      this.root.removeEventListener('change', this.handleChange);
      window.removeEventListener('resize', this.handleViewport);
      window.removeEventListener('scroll', this.handleViewport);
      if (this.viewportFrame) window.cancelAnimationFrame(this.viewportFrame);
      if (this.requestController) this.requestController.abort();
      delete this.root.bloomBundleSave;
    }
  }

  const rootsIn = scope => {
    const roots = Array.from(scope.querySelectorAll('[data-bb]'));
    if (scope.matches && scope.matches('[data-bb]')) roots.unshift(scope);
    return roots;
  };
  const init = scope => rootsIn(scope).forEach(root => {
    if (!root.bloomBundleSave) root.bloomBundleSave = new BundleSave(root);
  });
  window.BloomBundleSave = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => rootsIn(event.target).forEach(root => {
    if (root.bloomBundleSave) root.bloomBundleSave.destroy();
  }));
})();
